// Order logic types — shared validation result and state types
// These types are used by validation functions in both client and server
/**
 * The outcome of the filing's most recent fee calculation (SPEC-SOR-343):
 * `resolved` — the quote came back whole and `quote` carries its record;
 * `unresolved` — the LDS price row did not resolve (the existing
 * `EffectiveFeeQuote.unresolved` degrade); `degraded` — the API could not
 * produce the composed quote or could not read the account, and carries no
 * figures; `failed` — the calculation errored; `absent` — no calculation has
 * run for this draft (every persisted draft saved before this field existed
 * reads `absent`, since absence of the field IS `absent`).
 */
export const QUOTE_STATES = ['resolved', 'unresolved', 'degraded', 'failed', 'absent'];
/**
 * True exactly when the draft's fee calculation resolved and its record is
 * present — the ONE place the "is the fee quote resolved?" question is
 * computed. Consumers: the validator's not-resolved block, the
 * `courtFees.quoteState` completeness field, `paymentMethodRequired`, and the
 * portal's resolved-quote selector.
 */
export function isFeeQuoteResolved(courtFees) {
    return courtFees.quoteState === 'resolved' && !!courtFees.quote;
}
/**
 * True exactly when the resolved total is non-zero — the CLIENT's answer to
 * "does this filing need a stored card?", retained as PORTAL UX ONLY and
 * SUBORDINATE to the server's submit-time rule (R8).
 *
 * The server re-decides this same question when the filer submits, from the
 * order's persisted ServerQuote: `serverPaymentRequired` in
 * apps/api-gateway/src/services/server-payment-rule.ts — named in prose because
 * this package cannot import from api-gateway without inverting the workspace
 * dependency direction. That answer is the authority, and this one may never
 * demand a card the server would not require; where the two can differ, they
 * differ in the safe direction only, this rule abstaining on a card the server
 * would demand. What this function drives is the wizard's affordance: the
 * payment checklist, the `orderDetails.paymentMethodId` completeness field, and
 * the Submit-tab verdict.
 *
 * It ABSTAINS (answers false) whenever the quote record is absent, cleared or
 * non-resolved, leaving the submit-time decision to the server rather than
 * guessing from a record it cannot vouch for — a stale resolved record must
 * never demand a card, and the invalidation in the portal's fee-quote-record.ts
 * is what clears one whose inputs have moved.
 *
 * Keys on the record only: never on waiver status (a waived filing's resolved
 * total is $0.00 by FWR-11, so it lands in the zero branch) and never on the
 * legacy `fees.totalFees` accumulator, which reads 0 for an unresolved quote.
 */
export function paymentMethodRequired(courtFees) {
    return isFeeQuoteResolved(courtFees) && (courtFees.quote?.total ?? 0) > 0;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Merge multiple ValidationResults into one */
export function mergeResults(results) {
    const errors = results.flatMap(r => r.errors);
    const warnings = results.flatMap(r => r.warnings);
    return { valid: errors.length === 0, errors, warnings };
}
/** Create a passing validation result */
export function validResult() {
    return { valid: true, errors: [], warnings: [] };
}
/** Create a single-error result (convenience for our rules) */
export function errorResult(field, rule, message, source = 'ours') {
    return { valid: false, errors: [{ field, rule, message, source }], warnings: [] };
}
